import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import dayjs from "dayjs";
import classNames from "classnames";
import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from "@mui/lab";
import { mdiAlertCircleOutline, mdiTimelineClockOutline } from "@mdi/js";
import { Typography } from "@mui/material";

import AppBar from "components/AppBar";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import PageLayout from "components/PageLayout";
import PageLoader from "components/PageLoader";
import EventApi, { GetClosedEvents } from "api/event";
import useBoundStore from "store";
import VirtualizedList from "components/VirtualizedList";
import { EmEmoji } from "components/EmojiPicker/EmojiPicker";
import styles from "./groupTimeline.module.scss";

interface GroupTimelineProps {
  onBack?: () => void;
  onEventClick?: () => void;
}

function GroupTimeline(props: GroupTimelineProps) {
  const { onBack, onEventClick } = props;
  const [hasError, setHasError] = useState<boolean>(false);
  const [events, setEvents] = useState<Array<GetClosedEvents>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const isMobileDevice = useBoundStore((state) => state.miscellaneous.isMobileDevice);
  const selected = useBoundStore((state) => state.selected);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const eventApi = useMemo(() => new EventApi(), []);

  /**
   * On pressing browse back clear timeline event
   */
  useEffect(() => {
    if (navigationType === "POP") {
      selected.set({ timelineEvent: null });
    }
  }, [navigationType]);

  const getClosedEvents = useCallback(async () => {
    if (selected.groupId) {
      setHasError(false);
      setIsLoading(true);

      const { error, events: closedEvents } = await eventApi.getClosedEvents(selected.groupId);
      setIsLoading(false);

      if (error) {
        return setHasError(true);
      }

      setEvents(closedEvents);
    }

    return undefined;
  }, [eventApi, selected.groupId]);

  const renderAppBar = () => <AppBar title='Group timeline' onBack={onBack} />;

  /**
   * Handle timeline event click
   *
   * @param eventId
   */
  function handleEventClick(eventId: number): void {
    if (isMobileDevice) {
      // 🔴 Double check
      navigate({ pathname: `${location.pathname}/${eventId}` });
    } else if (onEventClick) {
      onEventClick();
    }
    selected.set({ timelineEvent: { id: eventId } });
  }

  useEffect(() => {
    getClosedEvents();
  }, [getClosedEvents]);

  /**
   * Generate items which will be used to display de virtualized list
   *
   * @returns
   */
  function generateVirtualizedList(): JSX.Element[] {
    const vrList: JSX.Element[] = [];

    events.forEach((event, index) =>
      vrList.push(
        <TimelineItem
          position={index % 2 ? "right" : "left"}
          className={styles.timelineItem}
          key={event.id}
          tabIndex={0}
          onClick={() => handleEventClick(event.id)}
          onKeyUp={(key) => (key.code === "Enter" ? handleEventClick(event.id) : undefined)}
        >
          <TimelineOppositeContent
            className={classNames({
              [styles.firstAndLastTimelineItem]: index === 0 || index === events.length - 1,
              [styles.timelineContent]: index > 0 && index < events.length - 1,
            })}
            variant='caption'
            color='text.secondary'
          >
            {dayjs(event.start_date).format("DD/MM/YYYY")}
            <br />
            {dayjs(event.start_date).format("HH:mm")}
          </TimelineOppositeContent>
          <TimelineSeparator
            className={classNames({
              [styles.separator]: events.length === 2 && index === 1,
            })}
          >
            {index === 0 ? null : <TimelineConnector />}
            <TimelineDot color='primary' variant='outlined'>
              <div className={styles.timelineDotContent}>
                <EmEmoji id={event.icon.id} size={24} skin={event.icon.skin} />
              </div>
            </TimelineDot>
            {index === events.length - 1 ? null : <TimelineConnector />}
          </TimelineSeparator>
          <TimelineContent
            className={classNames({
              [styles.firstAndLastTimelineItem]: index === 0 || index === events.length - 1,
              [styles.timelineContent]: index > 0 && index < events.length - 1,
              [styles.onlyTwoItems]: events.length === 2 && index === 1,
            })}
          >
            <Typography variant='body2'>{event.name}</Typography>
          </TimelineContent>
        </TimelineItem>
      )
    );

    return vrList;
  }

  if (hasError) {
    return (
      <PageLayout appBar={renderAppBar()}>
        <DataIconPlaceholder icon={mdiAlertCircleOutline} text='Unable to load events timeline' />
      </PageLayout>
    );
  }

  if (isLoading)
    return (
      <PageLayout appBar={renderAppBar()}>
        <PageLoader />
      </PageLayout>
    );

  return (
    <PageLayout appBar={renderAppBar()} paddingBottom={false}>
      {events.length === 0 ? (
        <DataIconPlaceholder
          icon={mdiTimelineClockOutline}
          text='The timeline is empty as there are no finished events to display'
        />
      ) : (
        <VirtualizedList estimateSize={160} rows={generateVirtualizedList()} style={{ paddingTop: "16px" }} />
      )}
    </PageLayout>
  );
}

export default GroupTimeline;
